import { Component } from '@theme/component';

/**
 * Added to Cart Dialog Component
 * 
 * Shows a confirmation dialog when an item is successfully added to the cart.
 * Displays product information and provides navigation options.
 *
 * @typedef {object} AddedToCartDialogRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} closeButton - The close button.
 * @property {HTMLElement} productInfo - The product info container.
 * @property {HTMLImageElement} productImage - The product image element.
 * @property {HTMLElement} productTitle - The product title element.
 * @property {HTMLElement} productOptions - The product options element.
 * @extends Component<AddedToCartDialogRefs>
 */
class AddedToCartDialogComponent extends Component {
  requiredRefs = ['dialog', 'closeButton', 'productImage', 'productTitle', 'productOptions'];

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Close dialog when clicking on backdrop
    this.refs.dialog.addEventListener('click', (event) => {
      if (event.target === this.refs.dialog) {
        this.closeDialog();
      }
    }, { signal });

    // Close dialog on escape key
    this.refs.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDialog();
    }, { signal });

    this.#setupRecommendationsDrag(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Enable mouse drag-to-scroll for recommendation carousel.
   * @param {AbortSignal} signal
   */
  #setupRecommendationsDrag(signal) {
    const list = this.querySelector('[ref="recommendationsList"]');
    if (!list) return;

    /** @type {number | null} */
    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;
    let suppressClick = false;

    const endDrag = () => {
      pointerId = null;
      list.classList.remove('is-dragging');
      if (moved) {
        suppressClick = true;
        window.setTimeout(() => {
          suppressClick = false;
        }, 0);
      }
      moved = false;
    };

    list.addEventListener('pointerdown', (event) => {
      const pointerEvent = /** @type {PointerEvent} */ (event);
      if (pointerEvent.pointerType !== 'mouse' || pointerEvent.button !== 0) return;

      pointerId = pointerEvent.pointerId;
      startX = pointerEvent.clientX;
      startScrollLeft = list.scrollLeft;
      moved = false;
    }, { signal });

    list.addEventListener('pointermove', (event) => {
      const pointerEvent = /** @type {PointerEvent} */ (event);
      if (pointerId !== pointerEvent.pointerId) return;

      const deltaX = pointerEvent.clientX - startX;
      if (Math.abs(deltaX) > 5) moved = true;
      if (!moved) return;

      list.classList.add('is-dragging');
      pointerEvent.preventDefault();
      list.scrollLeft = startScrollLeft - deltaX;
    }, { signal });

    list.addEventListener('pointerup', (event) => {
      const pointerEvent = /** @type {PointerEvent} */ (event);
      if (pointerId !== pointerEvent.pointerId) return;
      endDrag();
    }, { signal });

    list.addEventListener('pointercancel', (event) => {
      const pointerEvent = /** @type {PointerEvent} */ (event);
      if (pointerId !== pointerEvent.pointerId) return;
      endDrag();
    }, { signal });

    // Prevent link navigation when pointer moved as a drag gesture.
    list.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }, { signal, capture: true });

    // Prevent native image drag ghost.
    list.addEventListener('dragstart', (event) => {
      event.preventDefault();
    }, { signal });
  }

  /**
   * Shows the dialog with product information.
   * 
   * @param {object} productData - The product data to display
   * @param {string|number} [productData.id] - Product ID
   * @param {string} productData.title - Product title
   * @param {string} productData.image - Product image URL
   * @param {string} [productData.color] - Selected color option
   * @param {string} [productData.size] - Selected size option
   * @param {number} [productData.quantity] - Quantity added
   * @param {string} [productData.quantityLabel] - Localized quantity label
   * @param {object} [productData.options] - All variant options as key-value pairs
   */
  show(productData) {
    const { dialog, productImage, productTitle, productOptions } = this.refs;
    const placeholderImage = this.dataset.placeholderImage || '';

    // Show placeholder first to avoid stale image flash.
    if (placeholderImage) {
      productImage.src = placeholderImage;
      productImage.alt = '';
    }

    // Preload product image and replace placeholder when ready.
    if (productData.image) {
      const nextImage = new Image();
      nextImage.onload = () => {
        productImage.src = productData.image;
        productImage.alt = productData.title || '';
      };
      nextImage.src = productData.image;
    }

    // Set product title
    if (productData.title) {
      productTitle.textContent = productData.title;
    }

    // Clear existing options
    productOptions.innerHTML = '';
    
    if (productData.options && typeof productData.options === 'object') {
      const optionEntries = Object.entries(productData.options);
      
      optionEntries.forEach(([key, data]) => {
        const optionWrapper = document.createElement('span');
        optionWrapper.className = 'added-to-cart-dialog__option';
        
        // Label
        const label = document.createElement('span');
        label.className = 'added-to-cart-dialog__option-label';
        label.textContent = `${key}:`;
        optionWrapper.appendChild(label);
        
        // Content (Swatch or Value)
        if (data.swatch) {
          const swatch = document.createElement('span');
          swatch.className = 'swatch swatch--cart-dialog';
          swatch.style.setProperty('--swatch-background', data.swatch);
          optionWrapper.appendChild(swatch);
          
          // 텍스트 값은 제거함 (사용자 요청)
        } else {
          const value = document.createElement('span');
          value.className = 'added-to-cart-dialog__option-value';
          value.textContent = data.value || data;
          optionWrapper.appendChild(value);
        }
        
        productOptions.appendChild(optionWrapper);
      });
    }

    // Add Quantity if exists
    if (productData.quantity) {
      const quantityLabel = productData.quantityLabel || 'Qty';
      const qtyWrapper = document.createElement('span');
      qtyWrapper.className = 'added-to-cart-dialog__option';
      qtyWrapper.innerHTML = `<span class="added-to-cart-dialog__option-label">${quantityLabel}:</span><span class="added-to-cart-dialog__option-value">${productData.quantity}</span>`;
      
      productOptions.appendChild(qtyWrapper);
    }

    // Show the dialog
    dialog.showModal();
    dialog.focus();

    // Prevent background scroll
    document.body.style.overflow = 'hidden';

    if (productData.id) {
      this.fetchRecommendations(productData.id);
    }
  }

  /**
   * Fetch product recommendations
   * @param {string|number} productId
   */
  async fetchRecommendations(productId) {
    const container = this.querySelector('[ref="recommendationsContainer"]');
    const list = this.querySelector('[ref="recommendationsList"]');
    if (!container || !list) return;

    try {
      // Show skeleton and container while fetching
      const skeletonHTML = `
        <div class="cart-rcmd-card cart-rcmd-skeleton">
          <div class="cart-rcmd-card__image-wrapper cart-rcmd-skeleton__box"></div>
          <div class="cart-rcmd-card__info">
            <div class="cart-rcmd-skeleton__box cart-rcmd-skeleton__text"></div>
            <div class="cart-rcmd-skeleton__box cart-rcmd-skeleton__text cart-rcmd-skeleton__text--short"></div>
          </div>
        </div>
      `;
      list.innerHTML = Array(4).fill(skeletonHTML).join('');
      container.classList.remove('hidden');

      // @ts-ignore
      const rootUrl = window.Shopify?.routes?.root || '/';
      const url = `${rootUrl}recommendations/products?section_id=cart-recommendations-section&limit=4&intent=related&product_id=${productId}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch recommendations');
      
      const html = await response.text();
      const div = document.createElement('div');
      div.innerHTML = html;
      
      // Shopify returns HTML with shopify-section wrapper
      const recommendationsContent = div.querySelector('.shopify-section')?.innerHTML || div.innerHTML;
      
      if (recommendationsContent.trim().length > 0) {
        list.innerHTML = recommendationsContent;
      } else {
        container.classList.add('hidden');
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      container.classList.add('hidden');
    }
  }

  #isMobile() {
    return window.innerWidth < 1024;
  }

  /**
   * Closes the dialog with platform-appropriate animation.
   */
  closeDialog() {
    const { dialog } = this.refs;

    if (this.#isMobile()) {
      dialog.classList.add('dialog-closing-mobile');

      let closed = false;
      const onEnd = () => {
        if (closed) return;
        closed = true;
        dialog.classList.remove('dialog-closing-mobile');
        dialog.close();
        document.body.style.overflow = '';
      };
      dialog.addEventListener('animationend', onEnd, { once: true });

      setTimeout(onEnd, 300);
    } else {
      dialog.close();
      document.body.style.overflow = '';
    }
  }

  /**
   * Navigate to cart page
   */
  viewCart() {
    const cartUrl = this.dataset.cartUrl || '/cart';
    window.location.href = cartUrl;
  }
}

if (!customElements.get('added-to-cart-dialog-component')) {
  customElements.define('added-to-cart-dialog-component', AddedToCartDialogComponent);
}

export { AddedToCartDialogComponent };
